import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahServiceCutover } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

export const typePrefix = 'sah-service-cutover';

/** One replacement: this service gives way to that one. */
export interface CutoverPair {
  from_service_id: string;
  to_service_id: string;
}

/**
 * What a run is stated as: a cutover date and the replacements to make.
 *
 * There is no saved, resumable run behind the assistant: a provider who stops part way through
 * starts it again with the same inputs, and each step reports what is already carried across. So the
 * context travels with every request rather than being held.
 */
export interface CutoverContext {
  cutover_date: string;
  pairs: CutoverPair[];
}

/**
 * A replacement as the assistant's own builder holds it: ids to send, names to render.
 *
 * The names are carried on the pair because the pickers that produced them have usually moved on to
 * another category by the time the list below is read - and a pair the mandate supplied was never
 * produced by a picker at all.
 */
export interface CutoverPairOption extends CutoverPair {
  from_service_name: string;
  to_service_name: string;
}

/** A contribution category, as the mandate names the two ends of the move. */
interface CutoverCategory {
  id: string;
  sah_id: string;
  name: string;
}

/**
 * The cutover Services Australia has mandated, resolved against this company's own services.
 *
 * `pairs` is empty where there is nothing to offer - no personal care associations, or reference
 * data not yet split into periods - which is a legitimate answer and not an error.
 */
export interface CutoverMandate {
  cutover_date: string;
  from_category: CutoverCategory | null;
  to_category: CutoverCategory | null;
  sah_service_type_name: string | null;
  pairs: CutoverPairOption[];
}

/** A Support at Home service as one of step 1's pickers offers it. */
export interface CutoverServiceOption {
  service_id: string;
  name: string;
  sah_id: string;
  category_id: string;
  start_date: string;
  end_date: string | null;
  /**
   * The grouping a provider names a change by - "the personal care services". Shown to providers as
   * the Service Type, which is the vendor's own name for it.
   *
   * Prefixed `sah_` because bare `service_type_id` already means the LOCAL service type - an integer
   * - elsewhere in this assistant, and the two would otherwise collide.
   */
  sah_service_type_id: string | null;
  sah_service_type_name: string | null;
}

/**
 * A GET on a step, with the run's pairs flattened for the query string.
 *
 * A query string cannot carry an array of objects, so the pairs travel as two parallel repeated
 * parameters and the server zips them back up by position. Any extra parameters the step needs
 * (`service_type_ids` and so on) ride along untouched.
 *
 * Wrapped as a whole query descriptor rather than left as a `toQueryParams(params)` call the author
 * has to remember: forgetting it sends `pairs[from_service_id]=...`, which Express parses into a key
 * nothing reads, so the server sees a run with no replacements and rejects it. That failed silently
 * until the step was actually reached, and the message it produced ("at least one service to cut over
 * is required") pointed at the wrong thing entirely.
 */
const cutoverQuery =
  <TParams extends CutoverContext>(url: string) =>
  ({ cutover_date, pairs, ...rest }: TParams) => ({
    url,
    // `as const` so the literal survives into BaseQueryArg's union - widened to `string` it no
    // longer satisfies the method type and every call site fails to compile.
    method: 'GET' as const,
    params: {
      cutover_date,
      from_service_ids: pairs.map(({ from_service_id }) => from_service_id),
      to_service_ids: pairs.map(({ to_service_id }) => to_service_id),
      ...rest,
    },
  });

/**
 * Every preview answers the same two questions: what is outstanding, and is there anything
 * outstanding at all. `is_complete` is what carries a re-run past a step already done.
 */
export interface CutoverStep<TRow> {
  rows: TRow[];
  is_complete: boolean;
  /** Present on paged steps: how many rows exist in total, not how many came back. */
  total?: number;
  /**
   * Of `total`, how many cannot be moved at all. Counted server-side across every page, because a
   * count taken from the page on screen is only ever a count of that page.
   */
  blocked_total?: number;
}

export interface CutoverServiceTypeRow {
  service_type_id: number;
  name: string;
  /** Which service being cut over this row hangs off - two rows can otherwise look identical. */
  from_service_id: string;
  service_name: string;
  already_associated: boolean;
}

export interface CutoverPricePlanRow {
  plan_id: string;
  name: string;
  start_date: string;
  end_date: string;
  revision_id: string;
  revision_sequence: number;
  service_type_ids: number[];
  price_count: number;
  already_carried: boolean;
}

export interface CutoverBudgetRow {
  budget_id: string;
  reference: string;
  client_id: number;
  client_name: string | null;
  status: string | null;
  status_tag: string | null;
  start_date: string;
  end_date: string;
  plan_name: string | null;
  split_count: number;
  move_count: number;
  target_revision_id: string | null;
  blocked_reason: string | null;
}

export interface CutoverServiceTypeResult {
  created: number;
  skipped: number;
}

export interface CutoverPricePlanResult {
  revisions: Array<{ plan_id: string; revision_id: string }>;
  skipped: number;
}

/** Starting the budget step returns a run to poll, not the result of the work. */
export interface CutoverRunStarted {
  run_id: string;
  total: number;
}

/** A budget run, as polled. `processed` against `total` is the progress. */
export interface CutoverRun {
  run_id: string;
  /** Who started it, when that can still be resolved - the guard is per company, not per user. */
  started_by: string | null;
  status: 'running' | 'completed' | 'failed';
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
  total: number;
  processed: number;
  updated: number;
  lines_split: number;
  lines_moved: number;
  failed_count: number;
  failures: Array<{ budget_id: string; reference: string; reason: string }>;
}

export const sahServiceCutoverApi = createApi({
  reducerPath: 'sahServiceCutoverApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahServiceCutover }),
  tagTypes: ['sah-service-cutover'],
  endpoints: (builder) => ({
    getCutoverMandate: builder.query<CutoverMandate, void>({
      query: () => ({ url: '/mandate', method: 'GET' }),
      transformResponse: (payload) => unwrap<CutoverMandate>(payload),
    }),
    getCutoverServices: builder.query<
      { rows: CutoverServiceOption[] },
      { category_id: string; effective_on?: string; associated_only?: boolean }
    >({
      query: (params) => ({ url: '/services', method: 'GET', params }),
      transformResponse: (payload) => unwrap<{ rows: CutoverServiceOption[] }>(payload),
    }),
    getCutoverServiceTypes: builder.query<CutoverStep<CutoverServiceTypeRow>, CutoverContext>({
      query: cutoverQuery('/service-types'),
      transformResponse: (payload) => unwrap<CutoverStep<CutoverServiceTypeRow>>(payload),
    }),
    applyCutoverServiceTypes: builder.mutation<
      CutoverServiceTypeResult,
      CutoverContext & { service_type_ids: number[] }
    >({
      query: (body) => ({ url: '/service-types', method: 'POST', body }),
      transformResponse: (payload) => unwrap<CutoverServiceTypeResult>(payload),
    }),
    getCutoverPricePlans: builder.query<
      CutoverStep<CutoverPricePlanRow>,
      CutoverContext & { service_type_ids: number[] }
    >({
      query: cutoverQuery('/price-plans'),
      transformResponse: (payload) => unwrap<CutoverStep<CutoverPricePlanRow>>(payload),
    }),
    applyCutoverPricePlans: builder.mutation<
      CutoverPricePlanResult,
      CutoverContext & { plan_ids: string[]; service_type_ids: number[] }
    >({
      query: (body) => ({ url: '/price-plans', method: 'POST', body }),
      transformResponse: (payload) => unwrap<CutoverPricePlanResult>(payload),
    }),
    getCutoverBudgets: builder.query<
      CutoverStep<CutoverBudgetRow>,
      CutoverContext & { service_type_ids: number[]; status_ids: string[]; limit: number; offset: number }
    >({
      query: cutoverQuery('/budgets'),
      transformResponse: (payload) => unwrap<CutoverStep<CutoverBudgetRow>>(payload),
    }),
    // No budget ids in the body. A provider looking at thousands of budgets across a hundred pages
    // cannot tick them individually, so everything affected is in scope and unticking a row excludes
    // it - which also keeps the request small however large the run.
    applyCutoverBudgets: builder.mutation<
      CutoverRunStarted,
      CutoverContext & { service_type_ids: number[]; status_ids: string[]; excluded_budget_ids: string[] }
    >({
      query: (body) => ({ url: '/budgets', method: 'POST', body }),
      transformResponse: (payload) => unwrap<CutoverRunStarted>(payload),
    }),
    getCutoverRun: builder.query<CutoverRun, { run_id: string }>({
      query: ({ run_id }) => ({ url: `/runs/${run_id}`, method: 'GET' }),
      transformResponse: (payload) => unwrap<CutoverRun>(payload),
    }),
    getActiveCutoverRun: builder.query<CutoverRun | null, void>({
      query: () => ({ url: '/runs/active', method: 'GET' }),
      transformResponse: (payload) => unwrap<CutoverRun | null>(payload),
    }),
  }),
});

export const {
  useGetCutoverMandateQuery,
  useGetCutoverServicesQuery,
  useGetCutoverServiceTypesQuery,
  useApplyCutoverServiceTypesMutation,
  useGetCutoverPricePlansQuery,
  useApplyCutoverPricePlansMutation,
  useGetCutoverBudgetsQuery,
  useApplyCutoverBudgetsMutation,
  useGetCutoverRunQuery,
  useGetActiveCutoverRunQuery,
} = sahServiceCutoverApi;
